import type { ReactNode } from "react";
import { PICTOS, type PictoKind } from "@handvero/shared";

/*
 * Sicherheitszeichen für die Schulungen. Angezeigt werden die Original-Dateien der BGHM aus ./picto (siehe picto/QUELLEN.md);
 * die gezeichneten Nachbildungen unten sind nur der Rückfall, falls eine Datei fehlt. Die Original-Grafiken der Normen (ISO 7010, ASR A1.3) und der Gefahrstoff-Verordnung
 * sind urheberrechtlich geschützt; HandVero zeichnet sie deshalb selbst nach: gleiche Form, gleiche Farbe, gleiche Aussage
 * (grünes Quadrat = Rettung, rotes Quadrat = Brandschutz, blauer Kreis = Gebot, roter Ring mit Balken = Verbot,
 * gelbes Dreieck = Warnung, rote Raute = Gefahrstoff). Auf Normnummern wird bewusst verzichtet.
 */

const GREEN = "#00875A";
const RED = "#D6001C";
const BLUE = "#0067B2";
const YELLOW = "#F9B900";
const INK = "#111111";

type Glyph = (c: string) => ReactNode;
const line = { fill: "none", strokeLinecap: "round", strokeLinejoin: "round" } as const;

/** Strichmännchen, damit alle Zeichen dieselbe Figur benutzen. */
const Head = ({ x, y, r = 4.2, c }: { x: number; y: number; r?: number; c: string }) => <circle cx={x} cy={y} r={r} fill={c} />;

const GLYPHS: Record<string, Glyph> = {
  fluchtweg: (c) => (
    <>
      <path d="M40 14h10v36H40" stroke={c} strokeWidth="3.4" {...line} />
      <path d="M40 14v36" stroke={c} strokeWidth="0" />
      <Head x={24} y={19} c={c} />
      <path d="M23 26l-5 11 6 4 1 9M23 26l8 3 5-3M18 37l-7 4M23 26l-1 8 8 4" stroke={c} strokeWidth="3.6" {...line} />
      <path d="M32 40h7M36 36.5l3.500 3.500-3.500 3.500" stroke={c} strokeWidth="3" {...line} />
    </>
  ),
  "erste-hilfe": (c) => <path d="M26 12h12v14h14v12H38v14H26V38H12V26h14z" fill={c} />,
  notruftelefon: (c) => (
    <>
      <path d="M17 24c0-4 4-7 8-5l4 2c1.500.800 2 2.400 1.300 3.800l-1.600 3.200c-.5 1 0 2.200.8 3 2.500 2.800 5 5 8 7 .9.600 2 .6 3 .1l3.200-1.600c1.400-.7 3-.2 3.800 1.300l2 4c2 4-1 8-5 8C27 49 17 38 17 24z" fill={c} />
      <path d="M40 14c4 .8 8 4.800 8.800 8.800M40 19c2 .5 4 2.500 4.600 4.600" stroke={c} strokeWidth="3" {...line} />
    </>
  ),
  sammelstelle: (c) => (
    <>
      <Head x={32} y={26} r={3.600} c={c} />
      <Head x={24} y={29} r={3.200} c={c} />
      <Head x={40} y={29} r={3.200} c={c} />
      <path d="M26 44v-9a6 6 0 0112 0v9zM17 44v-7a5 5 0 017-4.600M47 44v-7a5 5 0 00-7-4.600" {...line} fill={c} stroke={c} strokeWidth="1.500" />
      <path d="M8 8l9 9M8 8v8M8 8h8M56 8l-9 9M56 8v8M56 8h-8M8 56l9-9M8 56v-8M8 56h8M56 56l-9-9M56 56v-8M56 56h-8" stroke={c} strokeWidth="3" {...line} />
    </>
  ),
  feuerloescher: (c) => (
    <>
      <rect x="22" y="26" width="16" height="25" rx="5" fill={c} />
      <rect x="26" y="18" width="8" height="8" rx="1.500" fill={c} />
      <path d="M24 17h14M38 21c6 0 9 4 9 10v6" stroke={c} strokeWidth="3" {...line} />
      <path d="M46 40l-3 7h6z" fill={c} />
    </>
  ),
  brandmelder: (c) => (
    <>
      <rect x="14" y="14" width="36" height="36" rx="5" fill="none" stroke={c} strokeWidth="3.400" />
      <circle cx="32" cy="32" r="10" fill="none" stroke={c} strokeWidth="3" />
      <circle cx="32" cy="32" r="5" fill={c} />
    </>
  ),
  "rauchen-verboten": (c) => (
    <>
      <rect x="10" y="36" width="30" height="6" rx="1.500" fill={c} />
      <rect x="40" y="36" width="12" height="6" rx="1.500" fill="none" stroke={c} strokeWidth="2.200" />
      <path d="M22 32c-3-3 3-5 0-9s3-5 1-9M32 32c-3-3 3-5 0-9" stroke={c} strokeWidth="2.600" {...line} />
    </>
  ),
  "feuer-verboten": (c) => (
    <path d="M32 11c1 7 10 11 10 22 0 8-5 13-10 13s-10-5-10-12c0-5 3-8 5-11 1 4 2 5 4 6-1-6-1-11 1-18z" fill={c} />
  ),
  "aufzug-brand": (c) => (
    <>
      <rect x="20" y="13" width="24" height="38" rx="2" fill="none" stroke={c} strokeWidth="3" />
      <path d="M32 18l-6 7h12zM32 46l-6-7h12z" fill={c} />
      <path d="M32 25v14" stroke={c} strokeWidth="0" />
    </>
  ),
  gehoerschutz: (c) => (
    <>
      <path d="M17 36V30a15 15 0 0130 0v6" stroke={c} strokeWidth="4" {...line} />
      <rect x="12" y="34" width="10" height="16" rx="4.500" fill={c} />
      <rect x="42" y="34" width="10" height="16" rx="4.500" fill={c} />
    </>
  ),
  augenschutz: (c) => (
    <>
      <path d="M8 31h6M50 31h6" stroke={c} strokeWidth="4" {...line} />
      <path d="M14 26h15c2 0 3 1 3 3v6c0 4-3 8-8 8h-3c-4 0-7-4-7-8z" fill={c} />
      <path d="M50 26H35c-2 0-3 1-3 3v6c0 4 3 8 8 8h3c4 0 7-4 7-8z" fill={c} />
    </>
  ),
  kopfschutz: (c) => (
    <>
      <path d="M13 38c0-12 8-20 19-20s19 8 19 20z" fill={c} />
      <rect x="9" y="38" width="46" height="6" rx="3" fill={c} />
      <rect x="29" y="12" width="6" height="12" rx="2" fill={c} />
    </>
  ),
  fussschutz: (c) => (
    <path d="M20 12h14v18c0 3 2 4 5 5l10 4c3 1 5 3 5 6v3H15V12z" fill={c} />
  ),
  handschutz: (c) => (
    <>
      <rect x="22" y="14" width="5.500" height="22" rx="2.750" fill={c} />
      <rect x="28.500" y="10" width="5.500" height="26" rx="2.750" fill={c} />
      <rect x="35" y="12" width="5.500" height="24" rx="2.750" fill={c} />
      <rect x="41.500" y="17" width="5.500" height="19" rx="2.750" fill={c} />
      <path d="M22 30h25v10c0 8-5 14-12 14s-12-5-14-10l-6-9c-1-2 1-4 3.500-3l3.500 3z" fill={c} />
      <rect x="18" y="52" width="26" height="6" rx="2" fill={c} />
    </>
  ),
  atemschutz: (c) => (
    <>
      <path d="M15 24h34c0 14-6 23-17 23S15 38 15 24z" fill={c} />
      <circle cx="21" cy="43" r="7" fill={c} stroke="#0067B2" strokeWidth="2" />
      <circle cx="43" cy="43" r="7" fill={c} stroke="#0067B2" strokeWidth="2" />
      <path d="M26 41h12M26 45h12" stroke="#0067B2" strokeWidth="2" {...line} />
      <path d="M8 22l7 3M56 22l-7 3" stroke={c} strokeWidth="3.600" {...line} />
    </>
  ),
  schutzkleidung: (c) => (
    <path d="M24 12l-12 6 4 10 6-2v26h28V26l6 2 4-10-12-6c-1 5-5 8-10 8s-9-3-10-8z" fill={c} />
  ),
  "haende-waschen": (c) => (
    <>
      <path d="M12 18h20a8 8 0 018 8v4" stroke={c} strokeWidth="4.500" {...line} />
      <rect x="8" y="14" width="8" height="8" rx="2" fill={c} />
      <path d="M40 34c2 3 3 5 3 6.500a3 3 0 01-6 0c0-1.500 1-3.500 3-6.500zM28 34c1.500 2.500 2.500 4.500 2.500 6a2.500 2.500 0 01-5 0c0-1.500 1-3.500 2.500-6z" fill={c} />
      <path d="M12 52c0-6 6-8 14-8h12c8 0 14 2 14 8z" fill={c} />
    </>
  ),
  "warn-allgemein": (c) => (
    <>
      <rect x="28.500" y="12" width="7" height="25" rx="3.500" fill={c} />
      <circle cx="32" cy="48" r="4.200" fill={c} />
    </>
  ),
  "warn-spannung": (c) => <path d="M38 8L16 36h13l-4 20 24-30H35z" fill={c} />,
  "warn-stolpern": (c) => (
    <>
      <rect x="8" y="46" width="30" height="7" rx="1.500" fill={c} />
      <Head x={44} y={15} c={c} />
      <path d="M43 22l-10 10 8 6M33 32l-10-5M41 38l5 10M43 22l7 8" stroke={c} strokeWidth="4" {...line} />
    </>
  ),
  "warn-rutschen": (c) => (
    <>
      <Head x={26} y={14} c={c} />
      <path d="M27 21l-4 14-9 6M27 21l12 6M23 35l12 6 9-2M27 21l-8 5" stroke={c} strokeWidth="4" {...line} />
      <path d="M10 54c5-3 5 3 10 0s5 3 10 0 5 3 10 0 5 3 10 0" stroke={c} strokeWidth="3" {...line} />
    </>
  ),
  "ghs-explosiv": (c) => (
    <path d="M32 6l5 14 13-8-5 15 14 3-12 8 9 11-14-2-2 14-8-12-9 11 1-14-14-2 11-9-9-12 14 3z" fill={c} />
  ),
  "ghs-flamme": (c) => <path d="M32 6c2 12 17 19 17 36 0 12-8 18-17 18S15 54 15 44c0-8 5-13 9-18 1 6 3 9 6 10-2-10-2-20 2-30z" fill={c} />,
  "ghs-brandfoerdernd": (c) => (
    <>
      <circle cx="32" cy="40" r="14" fill="none" stroke={c} strokeWidth="5" />
      <path d="M32 8c1 6 8 9 8 18 0 6-4 10-8 10s-8-4-8-10c0-4 2-6 4-9 1 3 2 4 3 4-1-5-1-9 1-13z" fill={c} />
    </>
  ),
  "ghs-gasflasche": (c) => (
    <>
      <rect x="22" y="22" width="20" height="38" rx="9" fill={c} />
      <rect x="28" y="6" width="8" height="14" rx="2" fill={c} />
      <path d="M22 18h20" stroke={c} strokeWidth="4" {...line} />
    </>
  ),
  "ghs-aetzend": (c) => (
    <>
      <path d="M8 14l12-4 4 12-12 4zM30 8l12 4-4 12-12-4z" fill={c} transform="translate(0 -2)" />
      <path d="M16 30c0 5 3 5 3 8M34 28c0 5 3 5 3 8" stroke={c} strokeWidth="3" {...line} />
      <path d="M8 52h30l6-8 8 4v8H8z" fill={c} />
      <path d="M46 28l4 8 4-8" stroke={c} strokeWidth="3" {...line} />
    </>
  ),
  "ghs-giftig": (c) => (
    <>
      <path d="M32 8c-9 0-15 6-15 14 0 5 3 8 5 10v5h20v-5c2-2 5-5 5-10 0-8-6-14-15-14z" fill={c} />
      <circle cx="26" cy="23" r="4" fill="#fff" />
      <circle cx="38" cy="23" r="4" fill="#fff" />
      <path d="M12 44l40 14M52 44L12 58" stroke={c} strokeWidth="5" {...line} />
    </>
  ),
  "ghs-reizend": (c) => (
    <>
      <rect x="28" y="10" width="8" height="28" rx="3.500" fill={c} />
      <circle cx="32" cy="50" r="5" fill={c} />
    </>
  ),
  "ghs-gesundheit": (c) => (
    <>
      <Head x={32} y={12} r={6} c={c} />
      <path d="M18 56V34c0-8 6-14 14-14s14 6 14 14v22z" fill={c} />
      <path d="M32 26l2.400 5 5.300.7-3.900 3.700 1 5.300-4.800-2.600-4.800 2.600 1-5.300-3.900-3.700 5.300-.7z" fill="#fff" />
    </>
  ),
  "ghs-umwelt": (c) => (
    <>
      <path d="M18 54V38M18 44l-7-8M18 40l7-9M18 36l-3-9" stroke={c} strokeWidth="3.400" {...line} />
      <path d="M30 48c4-7 12-7 18-3l6-3v12l-6-3c-6 4-14 4-18-3z" fill={c} />
      <path d="M8 56h48" stroke={c} strokeWidth="3" {...line} />
    </>
  ),
};

const WHITE_ON: PictoKind[] = ["rescue", "fire", "mandatory"];

/** Offizielle Dateien aus ./picto/<kennung>.svg haben Vorrang vor der Nachzeichnung (siehe picto/LIESMICH.md). */
const OFFICIAL = Object.fromEntries(
  Object.entries(import.meta.glob<string>("./picto/*.{svg,png,jpg,webp}", { eager: true, query: "?url", import: "default" })).map(([path, url]) => [path.replace(/^.*\/(.+)\.(?:svg|png|jpg|webp)$/, "$1"), url]),
);

/** Hinweis unter Folien mit Sicherheitszeichen: Quelle, wenn alle Zeichen offiziell sind, sonst der Nachzeichnungs-Hinweis. */
export function pictoNote(ids: string[], fallback: string): string {
  return ids.length && ids.every((id) => OFFICIAL[id])
    ? "Sicherheitszeichen: Quelle Berufsgenossenschaft Holz und Metall (BGHM), bghm.de/arbeitsschuetzer/praxishilfen/sicherheitszeichen."
    : fallback;
}

/** Ein Sicherheitszeichen mit seinem Rahmen. Unbekannte Kennungen zeigen nichts. */
export function Picto({ id, size = 72, label = true }: { id: string; size?: number; label?: boolean }) {
  const info = PICTOS[id];
  const glyph = GLYPHS[id];
  if (!info || !glyph) return null;
  const official = OFFICIAL[id];
  if (official) return <img className="picto" src={official} width={size} height={size} alt={label ? info.label : ""} />;
  const ink = WHITE_ON.includes(info.kind) ? "#fff" : INK;
  let frame: ReactNode;
  let body: ReactNode = glyph(ink);
  switch (info.kind) {
    case "rescue":
      frame = <rect x="2" y="2" width="60" height="60" rx="7" fill={GREEN} />;
      break;
    case "fire":
      frame = <rect x="2" y="2" width="60" height="60" rx="7" fill={RED} />;
      break;
    case "mandatory":
      frame = <circle cx="32" cy="32" r="30" fill={BLUE} />;
      break;
    case "prohibition":
      frame = <circle cx="32" cy="32" r="27" fill="#fff" stroke={RED} strokeWidth="6" />;
      body = (
        <>
          <g transform="translate(32 32) scale(0.78) translate(-32 -32)">{body}</g>
          <path d="M13 13l38 38" stroke={RED} strokeWidth="6" strokeLinecap="round" />
        </>
      );
      break;
    case "warning":
      frame = <path d="M32 5L60 55H4z" fill={YELLOW} stroke={INK} strokeWidth="3.400" strokeLinejoin="round" />;
      body = <g transform="translate(32 40) scale(0.5) translate(-32 -32)">{body}</g>;
      break;
    case "ghs":
      frame = <path d="M32 3L61 32 32 61 3 32z" fill="#fff" stroke={RED} strokeWidth="5.500" strokeLinejoin="round" />;
      body = <g transform="translate(32 32) scale(0.56) translate(-32 -32)">{body}</g>;
      break;
  }
  return (
    <svg className="picto" viewBox="0 0 64 64" width={size} height={size} role="img" aria-label={label ? info.label : undefined} aria-hidden={label ? undefined : true}>
      {frame}
      {body}
    </svg>
  );
}

/** Zeichen mit Beschriftung darunter, wie im Film und im Katalog. */
export function PictoCard({ id, size = 84 }: { id: string; size?: number }) {
  const info = PICTOS[id];
  if (!info) return null;
  return (
    <figure className="picto-card">
      <Picto id={id} size={size} label={false} />
      <figcaption>{info.label}</figcaption>
    </figure>
  );
}
